import { ArrowLeft, CalendarDays, Car, MapPin } from "lucide-react";
import * as React from "react";
import { Button } from "./Button";
import { RequestToBookBadge } from "./RequestToBookBadge";

export interface ResultsSearchParams {
  from: string;
  to: string;
  adults: number;
  children: number;
  infants: number;
  pets: number;
  category_id?: string;
  vehicle_id?: string;
  open_in_new_tab?: string;
}

export interface InventoryVehiclePhoto {
  id: string;
  name: string | null;
  versions: {
    original: string | null;
    card: string | null;
    thumb: string | null;
  };
}

/** The RV type renters filter by; several catalog classes can share one `renter_key`. */
export interface VehicleRvClass {
  slug: string;
  label: string;
  renter_key: string;
  renter_name: string;
  renter_description: string | null;
  renter_position: number;
  group: "drivable" | "towable" | "other";
}

export interface VehicleResult {
  id: string;
  name: string;
  description?: string;
  year?: number | null;
  category?: string | null;
  equipment_type?: string | null;
  /** The vehicle type from the catalog; labels the card when there is no equipment type. */
  vehicle_type?: { id: number; slug: string; label: string } | null;
  sleeps?: number | null;
  length_feet?: number | null;
  photos?: InventoryVehiclePhoto[];
  daily_inventory: Array<{
    date: string;
    currency: string;
    available: boolean;
    rate: number;
  }>;
  totalPrice?: number;
  averageRate?: number;
  /** Effective booking mode; `request` shows the Request to Book badge. */
  booking_mode?: "instant" | "request" | null;
  /** Renter-facing search fields the results filters read; see `VehicleInventory` in `@fm/api`. */
  rv_class?: VehicleRvClass | null;
  pets_allowed?: boolean;
  delivery_available?: boolean;
  festivals_events_allowed?: boolean;
  unlimited_mileage?: boolean;
  amenities?: string[];
  /**
   * The server's effective booking mode, for the Booking mode filter only.
   * The card's Request to Book badge keeps reading `booking_mode`.
   */
  search_booking_mode?: "instant" | "request";
}

export interface ResultsProps {
  vehicles: VehicleResult[];
  searchParams: ResultsSearchParams;
  isLoading: boolean;
  error: string | null;
  selectedVehicle: string | null;
  /**
   * Whether a search actually ran. Defaults to `true`, so every existing
   * caller keeps rendering the genuine "found nothing" copy when
   * `vehicles` is empty. Pass `false` only when there was nothing to
   * search from at all (no pickup point and no chosen location) — that
   * case gets its own copy, because "adjust your dates" is the wrong
   * advice when the missing thing is a location.
   */
  hasSearched?: boolean;
  /**
   * The search could not run because the pick-up or the return date is
   * missing. It asks for the dates (and for a location too when
   * `hasSearched` is `false`) instead of saying nothing was found.
   */
  missingDates?: boolean;
  theme?: "light" | "dark";
  className?: string;
  onVehicleSelect?: (vehicleData: {
    vehicle_id: string;
    vehicle_name: string;
    total_price: number;
    search_params: ResultsSearchParams;
  }) => void;
  onReserve?: (vehicleData: {
    vehicle_id: string;
    vehicle_name: string;
    total_price: number;
    search_params: ResultsSearchParams;
  }) => void;
  onBackToSearch?: () => void;
  /**
   * When provided, each vehicle card renders as a real anchor (`<a href>`)
   * instead of a click-only `<div>`. This enables native browser affordances
   * (open in new tab, middle-click, copy link). Plain left-clicks are still
   * intercepted and routed through `onReserve` for client-side navigation.
   */
  getVehicleHref?: (vehicle: VehicleResult) => string;
  showBackButton?: boolean;
  currency?: string;
  showReserveButton?: boolean;
  showSelectButton?: boolean;
  showHeader?: boolean;
  /**
   * Replaces the "found nothing" sentence when filters (not the search)
   * emptied the list, and `emptyActions` render under it (e.g. "Clear filters").
   */
  emptyMessage?: string;
  emptyActions?: React.ReactNode;
}

export function Results({
  vehicles,
  searchParams,
  isLoading,
  error,
  selectedVehicle,
  hasSearched = true,
  missingDates = false,
  theme = "light",
  className,
  onVehicleSelect,
  onReserve,
  onBackToSearch,
  getVehicleHref,
  showBackButton = true,
  currency = "USD",
  showReserveButton = false,
  showSelectButton = false,
  showHeader = true,
  emptyMessage,
  emptyActions,
}: ResultsProps) {
  const handleVehicleSelect = (vehicle: VehicleResult) => {
    onVehicleSelect?.({
      vehicle_id: vehicle.id,
      vehicle_name: vehicle.name,
      total_price: vehicle.totalPrice || 0,
      search_params: searchParams,
    });
  };

  const handleReserve = (vehicle: VehicleResult) => {
    onReserve?.({
      vehicle_id: vehicle.id,
      vehicle_name: vehicle.name,
      total_price: vehicle.totalPrice || 0,
      search_params: searchParams,
    });
  };

  const handleBackToSearch = () => {
    onBackToSearch?.();
  };

  const handleAnchorClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    vehicle: VehicleResult,
  ) => {
    // Let the browser handle modified clicks natively (open in new tab,
    // middle-click, etc.). Only intercept plain left-clicks to keep
    // client-side navigation via the onReserve callback.
    if (e.currentTarget.target === "_blank") {
      return;
    }

    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) {
      return;
    }
    e.preventDefault();
    handleReserve(vehicle);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
    }).format(amount);
  };

  const calculateDuration = () => {
    const start = new Date(searchParams.from + "T00:00:00");
    const end = new Date(searchParams.to + "T00:00:00");
    const diffTime = Math.abs(end.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const totalGuests =
    searchParams.adults +
    searchParams.children +
    searchParams.infants +
    searchParams.pets;
  const duration = calculateDuration();

  // Check if a vehicle is available for the entire duration
  const isVehicleAvailable = (vehicle: VehicleResult) => {
    return vehicle.daily_inventory.every((day) => day.available);
  };

  const themeClasses =
    theme === "dark" ? "bg-gray-900 text-white" : "bg-white text-gray-900";

  if (isLoading) {
    return (
      <div className={`animate-pulse ${className || ""}`}>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="bg-white rounded-lg overflow-hidden">
              <div className="aspect-[4/3] bg-gray-200 rounded-lg" />
              <div style={{ paddingTop: "5px" }}>
                <div className="h-4 bg-gray-200 rounded w-24 mb-1" />
                <div className="h-5 bg-gray-200 rounded w-3/4" />
                <div className="h-4 bg-gray-200 rounded w-2/3 mt-1" />
                <div className="h-5 bg-gray-200 rounded w-2/5 mt-2" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`text-center ${className || ""}`}>
        <div className="bg-white rounded-xl shadow-sm border border-red-200 p-8 max-w-md mx-auto">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Car className="h-8 w-8 text-red-600" />
          </div>
          <h2 className="text-xl font-semibold text-red-800 mb-2">
            Oops! Something went wrong
          </h2>
          <p className="text-red-600 mb-6">{error}</p>
          {showBackButton && (
            <Button
              onClick={handleBackToSearch}
              variant="outline"
              className="border-red-300 text-red-700 hover:bg-red-50"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Search
            </Button>
          )}
        </div>
      </div>
    );
  }

  if (missingDates && vehicles.length === 0) {
    return (
      <div className={`text-center ${className || ""}`}>
        <div className="bg-white rounded-xl shadow-sm border border-blue-200 p-8 max-w-md mx-auto">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CalendarDays className="h-8 w-8 text-blue-600" />
          </div>
          <h2 className="text-xl font-semibold text-blue-800 mb-2">
            {hasSearched
              ? "Choose your dates"
              : "Choose your dates and pickup location"}
          </h2>
          <p className="text-blue-600">
            {hasSearched
              ? "Pick a pick-up and a return date to see the RVs available for your trip."
              : "Pick a pick-up and a return date, then enter an address or choose one of our pickup locations."}
          </p>
        </div>
      </div>
    );
  }

  if (!hasSearched && vehicles.length === 0) {
    return (
      <div className={`text-center ${className || ""}`}>
        <div className="bg-white rounded-xl shadow-sm border border-blue-200 p-8 max-w-md mx-auto">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <MapPin className="h-8 w-8 text-blue-600" />
          </div>
          <h2 className="text-xl font-semibold text-blue-800 mb-2">
            Add a pickup location
          </h2>
          <p className="text-blue-600">
            We couldn&apos;t run a search without one. Enter an address, or
            choose one of our pickup locations.
          </p>
        </div>
      </div>
    );
  }

  if (vehicles.length === 0) {
    return (
      <div className={`text-center ${className || ""}`}>
        <div className="bg-white rounded-xl shadow-sm border border-yellow-200 p-8 max-w-md mx-auto">
          <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Car className="h-8 w-8 text-yellow-600" />
          </div>
          <h2 className="text-xl font-semibold text-yellow-800 mb-2">
            No vehicles found
          </h2>
          <p className="text-yellow-600">
            {emptyMessage ??
              "We couldn't find any vehicles matching your criteria. Try adjusting your search dates or guest count."}
          </p>
          {emptyActions}
        </div>
      </div>
    );
  }

  return (
    <div className={`space-y-6 ${themeClasses} ${className || ""}`}>
      {/* Header */}
      {showHeader && (
        <h2 className="text-xl font-semibold text-gray-700 mb-6">
          {vehicles?.length} RV Rental{vehicles?.length !== 1 ? "s" : ""}{" "}
          Available
        </h2>
      )}

      {/* Previous Header - Commented out for redesign
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Available Vehicles
            </h1>
            <p className="text-gray-600">
              Found{" "}
              <span className="font-semibold text-blue-600">
                {vehicles.length}
              </span>{" "}
              available vehicle{vehicles.length !== 1 ? "s" : ""} for your trip
            </p>
          </div>
          {showBackButton && (
            <Button
              onClick={handleBackToSearch}
              variant="outline"
              className="border-gray-300 text-gray-700 hover:bg-gray-50 font-medium"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Search
            </Button>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-6 text-sm">
          <div className="flex items-center gap-2 bg-blue-50 px-3 py-2 rounded-lg">
            <Calendar className="h-4 w-4 text-blue-600" />
            <span className="font-medium text-blue-800">
              {new Date(searchParams.from + "T00:00:00").toLocaleDateString()} -{" "}
              {new Date(searchParams.to + "T00:00:00").toLocaleDateString()}
            </span>
          </div>
          <div className="flex items-center gap-2 bg-green-50 px-3 py-2 rounded-lg">
            <Users className="h-4 w-4 text-green-600" />
            <span className="font-medium text-green-800">
              {totalGuests} {totalGuests === 1 ? "guest" : "guests"}
            </span>
          </div>
          <div className="flex items-center gap-2 bg-purple-50 px-3 py-2 rounded-lg">
            <Clock className="h-4 w-4 text-purple-600" />
            <span className="font-medium text-purple-800">
              {duration} {duration === 1 ? "day" : "days"}
            </span>
          </div>
        </div>
      </div>
      */}

      {/* Vehicle Results */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {vehicles.map((vehicle) => {
          const primaryPhoto = vehicle.photos?.[0];
          const photoUrl = primaryPhoto?.versions?.card;

          // Equipment type as the label, else the vehicle type's catalog label
          const categoryLabel =
            vehicle?.equipment_type || vehicle?.vehicle_type?.label || null;

          // Build specs string
          const specs = [
            vehicle?.sleeps ? `Sleeps ${vehicle?.sleeps}` : null,
            vehicle?.length_feet ? `Length ${vehicle?.length_feet}ft` : null,
          ]
            .filter(Boolean)
            .join(" • ");

          const href = getVehicleHref?.(vehicle);
          const cardClassName =
            "group bg-white rounded-lg overflow-hidden cursor-pointer";

          const cardInner = (
            <>
              {/* Image */}
              <div className="relative aspect-[4/3] bg-gray-100 overflow-hidden rounded-lg">
                {photoUrl ? (
                  <img
                    src={photoUrl}
                    alt={vehicle?.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Car className="h-12 w-12 text-gray-300" />
                  </div>
                )}
              </div>

              {/* Content */}
              <div style={{ paddingTop: "5px" }}>
                {/* Category with year */}
                {categoryLabel && (
                  <p
                    className="text-sm text-gray-400"
                    style={{ marginBottom: 0 }}
                  >
                    {categoryLabel}
                  </p>
                )}

                {/* Vehicle name */}
                <h3
                  className="text-base font-bold text-gray-900 line-clamp-1"
                  style={{ marginBottom: 0 }}
                >
                  {vehicle?.name}
                </h3>

                {/* Request to Book */}
                {vehicle?.booking_mode === "request" && (
                  <RequestToBookBadge className="mt-1" />
                )}

                {/* Specs */}
                {specs && (
                  <p
                    className="text-sm text-gray-900"
                    style={{ marginBottom: 0 }}
                  >
                    {specs}
                  </p>
                )}

                {/* Price */}
                <p className="text-base mt-2">
                  <span className="font-bold text-gray-900">
                    {formatCurrency(vehicle?.totalPrice || 0)}
                  </span>
                  <span className="text-gray-900" style={{ fontSize: "15px" }}>
                    {" "}
                    for {duration} night{duration !== 1 ? "s" : ""}
                  </span>
                </p>
              </div>
            </>
          );

          const openInNewTab = searchParams?.open_in_new_tab === "true";

          return href ? (
            <a
              key={vehicle?.id}
              href={href}
              onClick={(e) => handleAnchorClick(e, vehicle)}
              target={
                // support to open external links in a new tab using the query param `open_in_new_tab`
                openInNewTab ? "_blank" : undefined
              }
              rel={
                // support to open external links in a new tab using the query param `open_in_new_tab`
                openInNewTab ? "noopener noreferrer" : undefined
              }
              className={`block text-inherit no-underline ${cardClassName}`}
            >
              {cardInner}
            </a>
          ) : (
            <div
              key={vehicle?.id}
              className={cardClassName}
              onClick={() => handleReserve(vehicle)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleReserve(vehicle);
                }
              }}
            >
              {cardInner}
            </div>
          );
        })}
      </div>

      {/* Previous Card Design - Commented out for redesign
      <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {vehicles.map((vehicle) => {
          const primaryPhoto = vehicle.photos?.[0];
          const photoUrl = primaryPhoto?.versions?.card;

          return (
            <div
              key={vehicle.id}
              className="group bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100 hover:border-gray-200"
            >
              <div className="relative h-48 bg-gradient-to-br from-gray-100 to-gray-200 overflow-hidden">
                {photoUrl ? (
                  <img
                    src={photoUrl}
                    alt={vehicle.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center">
                      <Car className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                      <p className="text-sm text-gray-500 font-medium">
                        Image coming soon
                      </p>
                    </div>
                  </div>
                )}
                <div className="absolute top-4 right-4">
                  <Badge
                    variant="default"
                    className="bg-green-50 text-green-700 border-green-200 shadow-sm"
                  >
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Available
                  </Badge>
                </div>
              </div>

              <div className="p-6">
                <div className="mb-4">
                  <h3 className="text-xl font-bold text-gray-900 mb-1 group-hover:text-blue-600 transition-colors">
                    {vehicle.name}
                  </h3>
                  {vehicle.category && (
                    <p className="text-sm text-gray-500 font-medium">
                      {vehicle.category}
                    </p>
                  )}
                  {vehicle.description && (
                    <p className="text-sm text-gray-600 mt-2 line-clamp-2">
                      {vehicle.description}
                    </p>
                  )}
                </div>

                <div className="flex items-center gap-4 mb-4 text-sm text-gray-600">
                  <div className="flex items-center gap-1">
                    <Users className="h-4 w-4" />
                    <span className="font-medium">{totalGuests} guests</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Clock className="h-4 w-4" />
                    <span className="font-medium">{duration} days</span>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-600">
                      Total Price
                    </span>
                    <span className="text-2xl font-bold text-gray-900">
                      {formatCurrency(vehicle.totalPrice || 0)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">
                      Average per day
                    </span>
                    <span className="text-lg font-semibold text-blue-600">
                      {formatCurrency(vehicle.averageRate || 0)}
                    </span>
                  </div>
                </div>

                <div className="space-y-3">
                  {showSelectButton && (
                    <Button
                      onClick={() => handleVehicleSelect(vehicle)}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md"
                      disabled={selectedVehicle === vehicle.id}
                    >
                      {selectedVehicle === vehicle.id ? (
                        <>
                          <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                          Processing...
                        </>
                      ) : (
                        <>
                          <DollarSign className="h-4 w-4 mr-2" />
                          Select Vehicle
                        </>
                      )}
                    </Button>
                  )}

                  {showReserveButton && isVehicleAvailable(vehicle) && (
                    <Button
                      onClick={() => handleReserve(vehicle)}
                      variant="outline"
                      className="w-full border-2 border-blue-600 text-blue-600 hover:bg-blue-50 font-semibold py-3 rounded-lg transition-all duration-200"
                    >
                      <Calendar className="h-4 w-4 mr-2" />
                      Reserve Now
                    </Button>
                  )}

                  {showReserveButton && !isVehicleAvailable(vehicle) && (
                    <Button
                      variant="outline"
                      className="w-full border-2 border-gray-300 text-gray-500 cursor-not-allowed font-semibold py-3 rounded-lg"
                      disabled
                    >
                      <Calendar className="h-4 w-4 mr-2" />
                      Not Available
                    </Button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      */}

      {/* Footer */}
      <div className="bg-gray-50 rounded-xl p-6 text-center">
        <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
          <div className="w-2 h-2 bg-green-500 rounded-full"></div>
          <span>
            All prices are in <span className="font-semibold">{currency}</span>{" "}
            and include applicable taxes and fees
          </span>
        </div>
      </div>
    </div>
  );
}
